import React, { FC, ReactNode, useCallback, useMemo } from "react";
import Text from "@src/lib/ui/Text";
import useUserSelections from "./hooks/useUserSelections";
import {
  LocationGroupType,
  UserSelectionInput,
  UserType,
} from "@src/types.generated";
import {
  EmptyState,
  EmptyStateIcon,
  EmptyStateTitle,
  EmptyStateDescription,
} from "@src/lib/ui/EmptyState";
import CalendarOutlineIcon from "@src/lib/icons/18px/calendar-outline";
import CirclePlusOutlineIcon from "@src/lib/icons/18px/circle-plus-outline";
import useHiddenLocationGroupTypes from "@src/features/home/hooks/useHiddenLocationGroupTypes";
import {
  USER_SELECTION_FILTERS,
  UserSelectionFilterType,
} from "./SelectUsersDialog";
import { cloneDeep, toLower } from "lodash";
import { USER_SELECTION_ICON } from "./constants";
import { formatSentenceList } from "./utils";
import { cn } from "@src/lib/utils/cn";
import useUserSelectionPreviewState from "./hooks/useUserSelectionPreviewState";
import { AutomationItem } from "@src/features/sidebars/shared/constants";
import { useGetUsersForUserSelectionQuery } from "./hooks/useGetUsersForOneOffAssignment.generated";
import { Skeleton } from "@src/lib/ui/Skeleton";
import { pluralize, pluralizeVerb } from "@src/lib/utils/strings";

import XIcon from "@src/lib/icons/18px/x";

const IndentedLeftBorder: FC<{
  width?: number;
  showIndent?: boolean;
  children: ReactNode | ReactNode[];
}> = (props) => {
  return (
    <div
      className={cn(
        "flex self-stretch gap-3",
        props.showIndent ? "pl-4" : "pl-0",
      )}
    >
      {props.showIndent && (
        <div
          className="mb-4 self-stretch rounded-bl-xl border-b border-l border-gray-5"
          style={{ width: props.width ? props.width : 32 }}
        />
      )}
      <div
        className={cn(
          "flex-1 self-stretch flex flex-col",
          props.showIndent ? "gap-1" : "gap-3",
        )}
      >
        {props.children}
      </div>
    </div>
  );
};

const InlineWrapCard: FC<{
  children: ReactNode | ReactNode[];
  showCard?: boolean;
}> = ({ children, showCard = true }) => {
  return (
    <div
      className={cn(
        "inline-flex flex-wrap w-fit self-start items-center gap-1",
        showCard && "border border-input bg-background rounded-xl px-3 py-2",
      )}
    >
      {children}
    </div>
  );
};

type ToneName =
  | "blue"
  | "purple"
  | "gray"
  | "green"
  | "yellow"
  | "red"
  | "orange";

const toneClasses: Record<
  ToneName,
  { bg: string; border: string; label: string; icon: string }
> = {
  blue: {
    bg: "bg-blue-1",
    border: "border-blue-3",
    label: "text-blue-12",
    icon: "text-blue-6",
  },
  purple: {
    bg: "bg-purple-1",
    border: "border-purple-3",
    label: "text-purple-12",
    icon: "text-purple-6",
  },
  gray: {
    bg: "bg-gray-1",
    border: "border-gray-3",
    label: "text-gray-12",
    icon: "text-gray-6",
  },
  green: {
    bg: "bg-green-1",
    border: "border-green-3",
    label: "text-green-12",
    icon: "text-green-6",
  },
  yellow: {
    bg: "bg-yellow-1",
    border: "border-yellow-3",
    label: "text-yellow-12",
    icon: "text-yellow-6",
  },
  red: {
    bg: "bg-red-1",
    border: "border-red-3",
    label: "text-red-12",
    icon: "text-red-6",
  },
  orange: {
    bg: "bg-orange-1",
    border: "border-orange-3",
    label: "text-orange-12",
    icon: "text-orange-6",
  },
};

const RoundedPill: FC<{
  label: string;
  icon: React.ReactElement;
  onClick?: () => void;
  color?: string;
}> = ({ label, icon, onClick, color = "blue" }) => {
  const classes = toneClasses[color as ToneName];

  return (
    <div
      className={cn(
        "flex items-center h-6 pl-1.5 rounded-2xl border border-solid",
        onClick ? "pr-0 cursor-pointer" : "pr-3 cursor-auto",
        classes.bg,
        classes.border,
      )}
    >
      <div className={cn("flex items-center gap-0.5", classes.icon)}>
        {icon}
        <Text variant="compact-title" className={classes.label}>
          {label}
        </Text>
      </div>
      {onClick && (
        <div className="flex size-6 shrink-0 items-center justify-center">
          <XIcon className={cn("size-3", classes.icon)} onClick={onClick} />
        </div>
      )}
    </div>
  );
};

type Props = {
  input: UserSelectionInput;
  setInput?: (input: UserSelectionInput) => void;
  hideFilters?: UserSelectionFilterType[];
  lockedUserTypes?: UserType[];
  showUserTypeOptions?: boolean;
  pillColor?: string;
  showEmptyState?: boolean;
  useLocationSelectionCopy?: boolean;
  showCards?: boolean;
  emptyStateSubtitle?: string;
  item?: AutomationItem;
  showCounts?: boolean;
  /** Replaces the default "N users currently belong to this segment" line. */
  renderCountText?: (count: number) => React.ReactNode;
  countClassName?: string;
  className?: string;
  chatId?: string;
};

const UserSelectionPreview: FC<Props> = ({
  input,
  setInput,
  hideFilters,
  lockedUserTypes,
  pillColor = "gray",
  emptyStateSubtitle,
  showUserTypeOptions = false,
  showEmptyState = true,
  useLocationSelectionCopy = false,
  showCards = true,
  showCounts = false,
  renderCountText,
  countClassName,
  className,
  chatId,
}) => {
  const { hasSelection, nestLocationAndLocationGroupSelections } =
    useUserSelectionPreviewState({
      input: input,
      showUserTypeOptions: showUserTypeOptions,
      useLocationSelectionCopy: useLocationSelectionCopy,
      chatId: chatId,
    });

  const { data: userSelectData, loading } = useGetUsersForUserSelectionQuery({
    skip: !showCounts,
    variables: { input },
  });

  const count = showCounts
    ? userSelectData?.GetUsersForUserSelection.length
    : undefined;

  const hiddenFilters = useHiddenLocationGroupTypes();
  const visibleFilters = useMemo(
    () =>
      USER_SELECTION_FILTERS.filter(
        (filter) => !hiddenFilters.includes(filter as LocationGroupType),
      )
        .filter((x) => !hideFilters?.includes(x))
        .map((x) => {
          if (x === "people") {
            return "name";
          } else if (x === "hireDate") {
            return "hire date";
          } else {
            return x;
          }
        })
        .map((x) => toLower(x as string)),
    [hiddenFilters, hideFilters],
  );
  const subtitle = useMemo(() => {
    if (emptyStateSubtitle) return emptyStateSubtitle;

    return `Select ${useLocationSelectionCopy ? "locations" : "users"} by ${formatSentenceList(visibleFilters, "or")}`;
  }, [visibleFilters, useLocationSelectionCopy, emptyStateSubtitle]);

  return (
    <div
      className={cn(
        "flex flex-1 flex-col items-center self-stretch gap-2 bg-gray-1 p-3 rounded-xl [container-type:inline-size] [@container(min-width:476px)]:pr-[48px]",
        className,
      )}
    >
      {hasSelection && (
        <>
          <div className="flex flex-col gap-2 self-stretch">
            <UserTypesRoleGroupAndRolesCard
              input={input}
              pillColor={pillColor}
              setInput={setInput}
              showUserTypeOptions={showUserTypeOptions}
              showCards={showCards}
              lockedUserTypes={lockedUserTypes}
              chatId={chatId}
            />
            <IndentedLeftBorder
              showIndent={nestLocationAndLocationGroupSelections}
            >
              <LocationGroupsCard
                input={input}
                pillColor={pillColor}
                setInput={setInput}
                showUserTypeOptions={showUserTypeOptions}
                useLocationSelectionCopy={useLocationSelectionCopy}
                showCards={showCards}
                chatId={chatId}
              />
              <LocationsCard
                input={input}
                pillColor={pillColor}
                setInput={setInput}
                showUserTypeOptions={showUserTypeOptions}
                useLocationSelectionCopy={useLocationSelectionCopy}
                showCards={showCards}
                chatId={chatId}
              />
            </IndentedLeftBorder>
            {input.daysAfterHire && !isNaN(input.daysAfterHire) && (
              <IndentedLeftBorder showIndent>
                <DaysAfterHireCard input={input} pillColor={pillColor} />
              </IndentedLeftBorder>
            )}
          </div>
          <UsersCard
            input={input}
            pillColor={pillColor}
            setInput={setInput}
            showUserTypeOptions={showUserTypeOptions}
            showCards={showCards}
            chatId={chatId}
          />
          {showCounts && loading ? (
            <div className="w-full px-[8px] py-[9px]">
              <Skeleton className="h-[8px] w-[100px] bg-gray-7" />
            </div>
          ) : (
            count !== undefined && (
              <div className={cn("w-full px-[8px]", countClassName)}>
                <Text
                  multiline
                  variant="fine-print-title"
                  className={
                    count > 0 || renderCountText
                      ? "text-gray-9"
                      : "text-status-warning-text"
                  }
                >
                  {renderCountText ? (
                    renderCountText(count)
                  ) : count > 0 ? (
                    <>
                      {count} {pluralize("user", count)} currently{" "}
                      {pluralizeVerb("belong", count)} to this segment
                    </>
                  ) : (
                    <>No users currently belong to this segment</>
                  )}
                </Text>
              </div>
            )
          )}
        </>
      )}
      {!hasSelection && showEmptyState && (
        <EmptyState>
          <EmptyStateIcon className="size-8">
            <CirclePlusOutlineIcon className="size-4" />
          </EmptyStateIcon>
          <EmptyStateTitle>
            {`Select ${useLocationSelectionCopy ? "locations" : "users"}`}
          </EmptyStateTitle>
          <EmptyStateDescription>{subtitle}</EmptyStateDescription>
        </EmptyState>
      )}
    </div>
  );
};

const UserTypesRoleGroupAndRolesCard: FC<Props> = (props) => {
  const { selectedRoles, selectedRoleGroups } = useUserSelections(
    props.input,
    props.chatId,
  );
  const {
    showRoles,
    showRoleGroups,
    userTypesLabel,
    rolesLabel,
    roleGroupsLabel,
  } = useUserSelectionPreviewState({
    input: props.input,
    showUserTypeOptions: props.showUserTypeOptions,
    useLocationSelectionCopy: props.useLocationSelectionCopy,
    chatId: props.chatId,
  });
  const noUserTypesSelected = useMemo(() => {
    return (
      !props.input.allTrainees &&
      !props.input.allManagers &&
      !props.input.allAdmins
    );
  }, [props.input.allAdmins, props.input.allManagers, props.input.allTrainees]);
  const locationsOrLocationGroupsSelected = useMemo(() => {
    return (
      !!props.input.locationIds?.length ||
      !!props.input.locationGroupIds?.length
    );
  }, [props.input.locationGroupIds?.length, props.input.locationIds?.length]);
  if (!showRoles && !showRoleGroups) {
    return null;
  }

  return (
    <InlineWrapCard showCard={props.showCards}>
      {userTypesLabel && <Text variant="compact-title">{userTypesLabel}</Text>}
      {props.showUserTypeOptions && (
        <div className="flex flex-wrap gap-1">
          {props.input.everyoneAtOrgOrBrand && (
            <RoundedPill
              icon={USER_SELECTION_ICON["roles"]}
              label="Everyone"
              color={props.pillColor}
              onClick={
                props.setInput &&
                !props.lockedUserTypes?.includes(UserType.Employee)
                  ? () => {
                      const newInput = cloneDeep(props.input);
                      newInput.everyoneAtOrgOrBrand = false;
                      props.setInput?.(newInput);
                    }
                  : undefined
              }
            />
          )}
          {props.input.allTrainees && (
            <RoundedPill
              icon={USER_SELECTION_ICON["roles"]}
              label="Trainees"
              color={props.pillColor}
              onClick={
                props.setInput &&
                !props.lockedUserTypes?.includes(UserType.Employee)
                  ? () => {
                      const newInput = cloneDeep(props.input);
                      newInput.allTrainees = false;
                      props.setInput?.(newInput);
                    }
                  : undefined
              }
            />
          )}
          {props.input.allManagers && (
            <RoundedPill
              icon={USER_SELECTION_ICON["roles"]}
              label="Managers"
              color={props.pillColor}
              onClick={
                props.setInput &&
                !props.lockedUserTypes?.includes(UserType.Manager)
                  ? () => {
                      const newInput = cloneDeep(props.input);
                      newInput.allManagers = false;
                      props.setInput?.(newInput);
                    }
                  : undefined
              }
            />
          )}
          {props.input.allAdmins && (
            <RoundedPill
              icon={USER_SELECTION_ICON["roles"]}
              label="Admins"
              color={props.pillColor}
              onClick={
                props.setInput &&
                !props.lockedUserTypes?.includes(UserType.Admin)
                  ? () => {
                      const newInput = cloneDeep(props.input);
                      newInput.allAdmins = false;
                      props.setInput?.(newInput);
                    }
                  : undefined
              }
            />
          )}
        </div>
      )}
      {roleGroupsLabel && (
        <Text variant="compact-title">{roleGroupsLabel}</Text>
      )}
      {selectedRoleGroups.map((roleGroup) => (
        <RoundedPill
          key={roleGroup.id}
          icon={USER_SELECTION_ICON["departments"]}
          label={roleGroup.name}
          color={props.pillColor}
          onClick={
            props.setInput
              ? () => {
                  const newInput = cloneDeep(props.input);
                  newInput.roleGroupIds = newInput.roleGroupIds?.filter(
                    (x) => x !== roleGroup.id,
                  );
                  if (
                    newInput.roleIds?.length === 0 &&
                    newInput.roleGroupIds?.length === 0 &&
                    noUserTypesSelected &&
                    locationsOrLocationGroupsSelected
                  ) {
                    newInput.allTrainees = true;
                    newInput.allManagers = true;
                    newInput.allAdmins = true;
                  }
                  props.setInput?.(newInput);
                }
              : undefined
          }
        />
      ))}
      {rolesLabel && <Text variant="compact-title">{rolesLabel}</Text>}
      {selectedRoles.map((role) => (
        <RoundedPill
          key={role.id}
          icon={USER_SELECTION_ICON["roles"]}
          label={role.name}
          color={props.pillColor}
          onClick={
            props.setInput
              ? () => {
                  const newInput = cloneDeep(props.input);
                  newInput.roleIds = newInput.roleIds?.filter(
                    (x) => x !== role.id,
                  );
                  if (
                    newInput.roleIds?.length === 0 &&
                    newInput.roleGroupIds?.length === 0 &&
                    noUserTypesSelected &&
                    locationsOrLocationGroupsSelected
                  ) {
                    newInput.allTrainees = true;
                    newInput.allManagers = true;
                    newInput.allAdmins = true;
                  }
                  props.setInput?.(newInput);
                }
              : undefined
          }
        />
      ))}
    </InlineWrapCard>
  );
};

const LocationGroupsCard: FC<Props> = (props) => {
  const {
    showLocationGroups,
    locationGroupsLabel,
    showRoles,
    hasUserTypeRoleGroupsOrRolesFilters,
  } = useUserSelectionPreviewState({
    input: props.input,
    showUserTypeOptions: props.showUserTypeOptions,
    useLocationSelectionCopy: props.useLocationSelectionCopy,
    chatId: props.chatId,
  });
  const { selectedLocationGroups, selectedLocationGroupsCount } =
    useUserSelections(props.input, props.chatId);
  const isLastLocationFilter = useMemo(() => {
    return (
      (props.input.locationIds?.length || 0) +
        (props.input.locationGroupIds?.length || 0) ===
      1
    );
  }, [props.input.locationGroupIds?.length, props.input.locationIds?.length]);
  const allUserTypesSelected = useMemo(() => {
    return (
      props.input.allTrainees &&
      props.input.allManagers &&
      props.input.allAdmins
    );
  }, [props.input.allAdmins, props.input.allManagers, props.input.allTrainees]);
  const handleClick = useCallback(
    (id: string) => {
      if (props.setInput) {
        const newInput = cloneDeep(props.input);
        newInput.locationGroupIds = newInput.locationGroupIds?.filter(
          (x) => x !== id,
        );
        if (isLastLocationFilter && allUserTypesSelected) {
          newInput.allTrainees = false;
          newInput.allManagers = false;
          newInput.allAdmins = false;
          newInput.roleIds = [];
        }
        props.setInput(newInput);
      }
    },
    [allUserTypesSelected, isLastLocationFilter, props],
  );
  const getOnClickX = useCallback(
    (lgId: string) => {
      return props.setInput
        ? () => {
            handleClick(lgId);
          }
        : undefined;
    },
    [handleClick, props.setInput],
  );
  if (!showLocationGroups) {
    return null;
  }

  return (
    <InlineWrapCard showCard={props.showCards}>
      {!showRoles &&
        (hasUserTypeRoleGroupsOrRolesFilters ||
          props.useLocationSelectionCopy) && (
          <Text variant="compact-title">
            All {props.useLocationSelectionCopy ? "locations" : "users"}
          </Text>
        )}
      {!showRoles &&
        !hasUserTypeRoleGroupsOrRolesFilters &&
        !props.useLocationSelectionCopy && (
          <Text variant="compact-title">No user types</Text>
        )}
      {selectedLocationGroupsCount.FRANCHISE > 0 && (
        <>
          <Text variant="compact-title">{locationGroupsLabel?.FRANCHISE}</Text>
          {selectedLocationGroups.FRANCHISE.map((franchise) => (
            <RoundedPill
              key={franchise.id}
              icon={USER_SELECTION_ICON[LocationGroupType.Franchise]}
              label={franchise.name}
              color={props.pillColor}
              onClick={getOnClickX(franchise.id)}
            />
          ))}
        </>
      )}
      {selectedLocationGroupsCount.BRAND > 0 && (
        <>
          <Text variant="compact-title">{locationGroupsLabel?.BRAND}</Text>
          {selectedLocationGroups.BRAND.map((brand) => (
            <RoundedPill
              key={brand.id}
              icon={USER_SELECTION_ICON[LocationGroupType.Brand]}
              label={brand.name}
              color={props.pillColor}
              onClick={getOnClickX(brand.id)}
            />
          ))}
        </>
      )}
      {selectedLocationGroupsCount.REGION > 0 && (
        <>
          <Text variant="compact-title">{locationGroupsLabel?.REGION}</Text>
          {selectedLocationGroups.REGION.map((region) => (
            <RoundedPill
              key={region.id}
              icon={USER_SELECTION_ICON[LocationGroupType.Region]}
              label={region.name}
              color={props.pillColor}
              onClick={getOnClickX(region.id)}
            />
          ))}
        </>
      )}
    </InlineWrapCard>
  );
};

const LocationsCard: FC<Props> = (props) => {
  const { showLocations, locationsLabel } = useUserSelectionPreviewState({
    input: props.input,
    showUserTypeOptions: props.showUserTypeOptions,
    useLocationSelectionCopy: props.useLocationSelectionCopy,
    chatId: props.chatId,
  });
  const { selectedLocations } = useUserSelections(props.input, props.chatId);
  const isLastLocationFilter = useMemo(() => {
    return (
      (props.input.locationIds?.length || 0) +
        (props.input.locationGroupIds?.length || 0) ===
      1
    );
  }, [props.input.locationGroupIds?.length, props.input.locationIds?.length]);
  const allUserTypesSelected = useMemo(() => {
    return (
      props.input.allTrainees &&
      props.input.allManagers &&
      props.input.allAdmins
    );
  }, [props.input.allAdmins, props.input.allManagers, props.input.allTrainees]);
  if (!showLocations) {
    return null;
  }

  return (
    <InlineWrapCard showCard={props.showCards}>
      {locationsLabel && <Text variant="compact-title">{locationsLabel}</Text>}
      {selectedLocations.map((location) => (
        <RoundedPill
          key={location.id}
          icon={USER_SELECTION_ICON["locations"]}
          label={location.name}
          color={props.pillColor}
          onClick={
            props.setInput
              ? () => {
                  const newInput = cloneDeep(props.input);
                  newInput.locationIds = newInput.locationIds?.filter(
                    (x) => x !== location.id,
                  );
                  if (isLastLocationFilter && allUserTypesSelected) {
                    newInput.allTrainees = false;
                    newInput.allManagers = false;
                    newInput.allAdmins = false;
                    newInput.roleIds = [];
                  }
                  props.setInput?.(newInput);
                }
              : undefined
          }
        />
      ))}
    </InlineWrapCard>
  );
};

const DaysAfterHireCard: FC<Props> = (props) => {
  if (!props.input.daysAfterHire) {
    return null;
  }

  return (
    <InlineWrapCard showCard={props.showCards}>
      <RoundedPill
        icon={<CalendarOutlineIcon className="size-4 shrink-0" />}
        label={`${props.input.daysAfterHire} ${
          props.input.daysAfterHire === 1 ? "day" : "days"
        } after hire`}
        color={props.pillColor}
      />
    </InlineWrapCard>
  );
};

const UsersCard: FC<Props> = (props) => {
  const { showUsers, usersLabel } = useUserSelectionPreviewState({
    input: props.input,
    showUserTypeOptions: props.showUserTypeOptions,
    useLocationSelectionCopy: props.useLocationSelectionCopy,
    chatId: props.chatId,
  });
  const { selectedUsers } = useUserSelections(props.input, props.chatId);
  if (!showUsers) {
    return null;
  }
  return (
    <InlineWrapCard showCard={props.showCards}>
      <Text variant="compact-title">{usersLabel}</Text>
      {selectedUsers.map((user) => (
        <RoundedPill
          key={user.id}
          icon={USER_SELECTION_ICON["people"]}
          label={user.name}
          color={props.pillColor}
          onClick={
            props.setInput
              ? () => {
                  const newInput = cloneDeep(props.input);
                  newInput.userIds = newInput.userIds?.filter(
                    (x) => x !== user.id,
                  );
                  props.setInput?.(newInput);
                }
              : undefined
          }
        />
      ))}
    </InlineWrapCard>
  );
};

export default UserSelectionPreview;
